import { Profile } from '@/services/profiles';
import { calculateDistance, formatDistance } from '@/utils/distance';
import styles from './DiscoverCard.module.css';

type DistanceUnit = 'm' | 'ft';

interface DiscoverCardProps {
  profile: Profile;
  onPass?: (profile: Profile) => void;
  onLike?: (profile: Profile) => void;
  distanceUnit?: DistanceUnit;
  viewerLocation?: { latitude: number; longitude: number } | null;
}

const initialsFromName = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

const formatMetadata = (metadata: Record<string, unknown>) =>
  Object.entries(metadata)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(' · ');

const getDistanceDisplay = (
  profileLocation: { latitude: number; longitude: number } | undefined,
  viewerLocation: { latitude: number; longitude: number } | null | undefined,
  unit: DistanceUnit,
) => {
  if (!profileLocation || !viewerLocation) {
    return null;
  }

  const distanceInMeters = calculateDistance(
    viewerLocation.latitude,
    viewerLocation.longitude,
    profileLocation.latitude,
    profileLocation.longitude,
  );

  return formatDistance(distanceInMeters, unit);
};

export const DiscoverCard = ({
  profile,
  onPass,
  onLike,
  distanceUnit = 'm',
  viewerLocation,
}: DiscoverCardProps) => {
  const initials = initialsFromName(profile.name);
  const headline = profile.metadata?.headline ?? profile.metadata?.title ?? undefined;
  const metaSummary =
    profile.metadata && Object.keys(profile.metadata).length > 0
      ? formatMetadata(profile.metadata)
      : undefined;

  let subline: string | undefined;
  if (headline) {
    subline = String(headline);
  } else if (profile.location) {
    subline = `${profile.location.latitude.toFixed(3)}, ${profile.location.longitude.toFixed(3)}`;
  }

  let bioContent: string;
  if (profile.description && profile.description.trim().length > 0) {
    bioContent = profile.description;
  } else if (metaSummary) {
    bioContent = metaSummary;
  } else {
    bioContent = "This profile hasn't added a description yet.";
  }

  return (
    <article className={styles.card} aria-label={`Profile card for ${profile.name}`}>
      <header className={styles.header}>
        <div className={styles.avatar} aria-hidden>
          {initials || 'AH'}
        </div>
        <div className={styles.title}>
          <span>{profile.name}</span>
          {subline && <span>{subline}</span>}
        </div>
      </header>

      <p className={styles.bio}>{bioContent}</p>

      <div className={styles.chips}>
        {(profile.interests ?? []).slice(0, 6).map((interest) => (
          <span key={interest} className={styles.chip}>
            {interest}
          </span>
        ))}
      </div>

      <div className={styles.metaRow}>
        <span>{profile.shortid ? `Share code: ${profile.shortid}` : profile.id}</span>
        {(() => {
          const distance = getDistanceDisplay(
            profile.location,
            viewerLocation,
            distanceUnit,
          );
          return distance ? <span>{distance}</span> : null;
        })()}
      </div>

      <footer className={styles.actions}>
        <button
          type="button"
          className={styles.pass}
          onClick={() => onPass?.(profile)}
          aria-label={`Skip ${profile.name}`}
        >
          Pass
        </button>
        <button
          type="button"
          className={styles.like}
          onClick={() => onLike?.(profile)}
          aria-label={`Say hi to ${profile.name}`}
        >
          <span aria-hidden role="img" className={styles.waveEmoji}>
            👋
          </span>
          Say hi
        </button>
      </footer>
    </article>
  );
};
